import { useMemo, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useHistory, useParams, Link as RouterLink } from 'react-router-dom';
import { Breadcrumb, ButtonToolbar, ButtonGroup, DropdownButton, Dropdown, Button } from 'react-bootstrap';
import { TooltipControl } from 'src/components/base/Tooltip';
import { SubmitAction } from 'src/components/base/SubmitAction';
import { LargeContainer, SimpleHeader, ButtonLink, SearchField } from 'src/components/base/Elements';
import { TrapSwitcher, EventStatus, EventPriority, EventSignal, EventBattery } from 'src/components/Trap/Elements';
import { Table } from 'src/components/base/Table';
import { GroupSettings } from './GroupSettings';
import { useModalDialog } from 'src/components/base/ModalDialog';
import { useFetchLocation } from 'src/utils/hooks';
import { useFetchTraps, useUpdateTrap } from 'src/model/traps';
import { useFetchGroup, useLastVisitedGroup } from 'src/model/groups';
import { useCurrentWorkspace } from 'src/model/workspaces';
import { isInteractOrOutsideElement, isSystemGroup } from 'src/utils/helpers';
import { formatFromISO } from 'src/utils/datefns';
import { hasPermission, PERMISSION_TRAP_EDIT, PERMISSION_GROUP_EDIT } from 'src/api/permissions';
import { ITEMS_PER_PAGE, SIM_ACTIVE, SIM_EXPIRING, SIM_BLOCKED, SIM_TERMINATED } from 'src/constants';

const TRAPS_FILTER = [
  { key: null, title: __('All Traps') },
  { key: 'alarms', title: __('Alarms') },
  { key: 'no_communication', title: __('No Communication') },
  { key: 'paused_subscriptions', title: __('Paused') },
  { key: 'expiring_subscriptions', title: __('Expires Soon') },
  { key: 'expired_subscriptions', title: __('Expired') },
];

export const GroupOverview = observer(() => {
  const history = useHistory();
  const params = useParams();
  const group_id = !isNaN(Number.parseInt(params.id)) ? +params.id : undefined;
  if (group_id === undefined) { history.replace('/traps') }

  const groupSettingsDialog = useModalDialog();

  const isSpecialGroup = group_id === 0 || group_id === -1;
  useLastVisitedGroup(group_id);

  const { pageIndex, sortBy, search, filter, setFetchLocation } = useFetchLocation();
  const { data: group, mutate: mutateGroup } = useFetchGroup({ group_id: group_id === 0 ? undefined : group_id });
  const { workspace: { workspace_id, permissions } } = useCurrentWorkspace(group.workspace_id);

  const { data, mutate } = useFetchTraps({ group_id, workspace_id, pageIndex, sortBy, search, filterKey: filter });
  const [updateTrapAction] = useUpdateTrap(mutate);

  const groupName = group_id === 0 ? __('All Traps') : group?.group_name;
  const isSystemManagedGroup = isSystemGroup(group);

  function handleFetchData({ pageIndex, sortBy }) {
    setFetchLocation({ pageIndex, sortBy, search });
  }

  function handleOnSearch(value) {
    setFetchLocation({ pageIndex: 0, sortBy, search: value, filter });
  }

  function handleOnFilter(key) {
    setFetchLocation({ pageIndex, sortBy, search, filter: key });
  }

  async function handleSwitchTrap(trap_id, is_switched_on) {
    await updateTrapAction({ trap_id, is_switched_on });
  }

  const handleClickBuildingMaps = useCallback(() => {
    if (!group_id || isSystemManagedGroup) { return; }
    history.push(`/groups/${group_id}/maps`);
  }, [group_id, history, isSystemManagedGroup]);

  async function handleClickGroupSettings() {
    if (isSystemManagedGroup) { return; }

    const result = await groupSettingsDialog.show({ group });
    if (result) {
      if (!result.group_id) { return history.replace('/traps') }
      mutateGroup();
      mutate();
    }
  }

  const columns = useMemo(
    () => [
      {
        accessor: 'event_priority',
        disableSortBy: true,
        Cell: ({ row: { original } }) => <EventPriority priority={original.event_priority} isCaught={original.is_caught} eventName={original.event_name} />,
        style: { width: 22 },
      },
      {
        Header: __('Trap Name'),
        accessor: 'trap_name',
        Cell: ({ row: { original } }) => <TrapName name={original.trap_name} notes={original.notes} />,
        style: { width: '20%' },
      },
      {
        Header: __('#'),
        headerClassName: 'text-end',
        accessor: 'serial_no',
        style: { width: 140, textAlign: 'end' },
      },
      {
        Header: __('Status'),
        accessor: 'time',
        Cell: ({ row: { original } }) => <TrapsStatus item={original} />,
      },
      {
        Header: __('Battery'),
        headerClassName: 'text-end',
        accessor: 'battery',
        Cell: ({ row: { original } }) => <EventBattery battery={original.battery} voltage={original.battery_full} />,
        style: { width: 100, textAlign: 'end' },
      },
      {
        Header: __('Signal'),
        headerClassName: 'text-end',
        accessor: 'signal',
        Cell: ({ row: { original } }) => <EventSignal signal={original.signal} rssi={original.signal_raw} />,
        style: { width: 100, textAlign: 'end' },
      },
      {
        Header: __('OFF/ON'),
        headerClassName: 'text-end',
        accessor: 'is_switched_on',
        Cell: ({ row: { original } }) => <TrapSwitcher type={original.trap_type}
          value={original.is_switched_on} onChange={value => handleSwitchTrap(original.trap_id, value)}
        />,
        style: { width: 100, textAlign: 'end' },
      },
    ],
    []
  );

  const getRowProps = useCallback(({ original: { trap_id } }) => (
    {
      onClick: event => !isInteractOrOutsideElement(event) && history.push(`/trap/${trap_id}`),
      className: 'cursor-pointer',
    }
  ), []);

  return (
    <LargeContainer className="mb-4">
      <SimpleHeader className="mt-4 mb-4">
        <Breadcrumb>
          <Breadcrumb.Item linkAs={RouterLink}
            linkProps={{ to: '/traps', style: { textDecoration: 'none' } }}>
            <i className="bi bi-house-fill" aria-hidden="true" /><span className="visually-hidden">{__('Trap Groups')}</span>
          </Breadcrumb.Item>
          <Breadcrumb.Item active>{groupName}</Breadcrumb.Item>
        </Breadcrumb>
      </SimpleHeader>

      <ButtonToolbar className="justify-content-between mb-5">
        <SearchField style={{ width: 350 }} value={search} onSearch={handleOnSearch} placeholder={__('Search (name, serial#, note)')} />

        <div>
          <DropdownButton className="me-2" style={{ width: 200 }} as={ButtonGroup}
            title={TRAPS_FILTER[TRAPS_FILTER.findIndex(val => val.key == filter) ?? 0].title} variant="outline-primary" onSelect={handleOnFilter}
          >
            {TRAPS_FILTER.map(({ key, title }) => <Dropdown.Item key={key} eventKey={key}>{title}</Dropdown.Item>)}
          </DropdownButton>

          <ButtonGroup aria-label="Third group">
            <TooltipControl tooltip={__('Catches')} placement="top">
              {({ ref, ...triggerHandler }) => (
                <span ref={ref} tabIndex={0} {...triggerHandler} className="btn-group-tooltip-wrapper d-inline-flex">
                  <ButtonLink
                    variant="outline-primary"
                    to={`/groups/${group_id}/catches`}
                    className="d-inline-flex align-items-center justify-content-center"
                    aria-label={__('Catches')}
                  >
                    <i className="bi bi-bar-chart-line-fill" aria-hidden="true" />
                    <span className="visually-hidden">{__('Catches')}</span>
                  </ButtonLink>
                </span>
              )}
            </TooltipControl>
            {hasPermission(permissions, PERMISSION_TRAP_EDIT) &&
              <TooltipControl tooltip={__('Export Traps')} placement="top">
                {({ ref, ...triggerHandler }) => (
                  <span ref={ref} tabIndex={0} {...triggerHandler} className="btn-group-tooltip-wrapper d-inline-flex">
                    <SubmitAction
                      action={`${config.api.serverUrl}/exporttraps`}
                      params={{ group_id }}
                      variant="outline-primary"
                      className="d-inline-flex align-items-center justify-content-center"
                      aria-label={__('Export Traps')}
                    >
                      <i className="bi bi-cloud-arrow-down bi-x1-3" aria-hidden="true" />
                      <span className="visually-hidden">{__('Export Traps')}</span>
                    </SubmitAction>
                  </span>
                )}
              </TooltipControl>
            }
            {MINKS_BRAND === 'trapsensor' && !isSpecialGroup && !isSystemManagedGroup &&
              <TooltipControl tooltip={__('Building Maps')} placement="top">
                <Button
                  variant="outline-primary"
                  onClick={handleClickBuildingMaps}
                  className="d-inline-flex align-items-center justify-content-center"
                  aria-label={__('Building Maps')}
                >
                  <i className="bi bi-map" aria-hidden="true" />
                  <span className="visually-hidden">{__('Building Maps')}</span>
                </Button>
              </TooltipControl>
            }
            {!isSpecialGroup && !isSystemManagedGroup && hasPermission(permissions, PERMISSION_GROUP_EDIT) &&
              <TooltipControl tooltip={__('Group Settings')} placement="top">
                <Button
                  variant="outline-primary"
                  onClick={handleClickGroupSettings}
                  className="d-inline-flex align-items-center justify-content-center"
                  aria-label={__('Group Settings')}
                >
                  <i className="bi bi-gear-fill" aria-hidden="true" />
                  <span className="visually-hidden">{__('Group Settings')}</span>
                </Button>
              </TooltipControl>
            }
            {hasPermission(permissions, PERMISSION_TRAP_EDIT) &&
              <TooltipControl tooltip={__('New Trap')} placement="top">
                {({ ref, ...triggerHandler }) => (
                  <span ref={ref} tabIndex={0} {...triggerHandler} className="btn-group-tooltip-wrapper d-inline-flex">
                    <ButtonLink
                      variant="outline-primary"
                      to={`/trap/new?group_id=${group_id}`}
                      className="d-inline-flex align-items-center justify-content-center"
                      aria-label={__('New Trap')}
                    >
                      <i className="bi bi-plus bi-x2" aria-hidden="true" />
                      <span className="visually-hidden">{__('New Trap')}</span>
                    </ButtonLink>
                  </span>
                )}
              </TooltipControl>
            }
          </ButtonGroup>
        </div>
      </ButtonToolbar>

      <Table columns={columns} data={data.items} getRowProps={getRowProps}
        pageIndex={pageIndex} pageCount={data.pageCount} pageSize={ITEMS_PER_PAGE} sortBy={sortBy}
        fetchData={handleFetchData}
      />

      <GroupSettings {...groupSettingsDialog.register()} />
    </LargeContainer>
  )
})

const TrapName = ({ name, notes }) => (
  <TooltipControl tooltip={notes} placement="top">
    <span className="text-break text-wrap fw-bold">{name}</span>
  </TooltipControl>
)

const TrapsStatus = ({ item }) => {
  const { workspace_id, time, event_title, event_subtitle, sim_card_status, sim_card_expired_at } = item;
  const isOwnTrap = workspace_id === 0;

  return (
    <>
      {time && <>{formatFromISO(time, 'Pp')}<br /></>}
      <EventStatus title={event_title} subtitle={event_subtitle} />
      {sim_card_status !== SIM_ACTIVE &&
        <span className="text-danger">
          <br />
          {sim_card_status === SIM_TERMINATED &&
            <span>{__('Subscription expired.')} {__('Please contact customer support.')}</span>
          }

          {sim_card_status === SIM_BLOCKED &&
            <span>{isOwnTrap ? __('Subscription expired.') : __('SIM card was blocked. Contact Group Owner to unblock.')}</span>
          }

          {sim_card_status === SIM_EXPIRING &&
            <span>{isOwnTrap ? __('Subscription will expire soon.') : __('SIM card will expire soon. Contact Group Owner to prolong.')}</span>
          }
        </span>
      }
    </>
  )
}
